import { isValidNip, type NipLookupDto, nipWarning, normalizeNip } from '@bqcrm/shared';
import { Button, Group, Loader, Paper, Stack, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconAlertTriangle, IconDownload } from '@tabler/icons-react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, errorText } from '../api';
import { VatStatusBadge } from './Badges';

/**
 * Pobieranie danych firmy po NIP (GUS REGON + biała lista VAT, docs/10 §17). Zapytanie idzie dopiero,
 * gdy użytkownik wpisze poprawny NIP (`active`) — otwarcie edycji istniejącej firmy nie pyta rejestrów.
 */
export function useNipLookup(nip: string, active: boolean) {
  const [debounced] = useDebouncedValue(normalizeNip(nip), 400);
  return useQuery({
    queryKey: ['nip-lookup', debounced],
    queryFn: () => api<NipLookupDto>(`/parties/nip-lookup/${debounced}`),
    enabled: active && isValidNip(debounced),
    staleTime: Infinity,
    retry: false,
  });
}

export function NipInput({ value, onChange, loading }: { value: string; onChange: (v: string) => void; loading?: boolean }) {
  const digits = normalizeNip(value);
  const error = digits.length === 10 && !isValidNip(digits) ? 'Niepoprawny NIP — błędna cyfra kontrolna' : digits.length > 10 ? 'NIP ma 10 cyfr' : undefined;
  return (
    <TextInput
      label="NIP"
      placeholder="10 cyfr — dane firmy pobiorą się same"
      inputMode="numeric"
      value={value}
      error={error}
      rightSection={loading ? <Loader size="xs" /> : undefined}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
}

const address = (c: NonNullable<NipLookupDto['company']>) => [c.street, [c.postalCode, c.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');

/** Wynik sprawdzenia NIP: status VAT, źródło danych, ostrzeżenia (nie blokują zapisu). */
export function NipLookupInfo({ lookup, onApply }: { lookup: UseQueryResult<NipLookupDto>; onApply?: (d: NipLookupDto) => void }) {
  if (lookup.isError) return <Text size="xs" c="red">Nie udało się sprawdzić NIP: {errorText(lookup.error)}</Text>;
  const d = lookup.data;
  if (!d) return null;
  const warning = nipWarning(d);
  const c = d.company;
  return (
    <Paper withBorder p="xs" radius="sm">
      <Stack gap={4}>
        <Group gap="xs" justify="space-between" wrap="nowrap">
          <Group gap="xs">
            {d.vat ? <VatStatusBadge check={d.vat} /> : <Text size="xs" c="dimmed">Status VAT nieznany</Text>}
            {c && (
              <Text size="xs" c="dimmed">
                {d.source === 'regon' ? 'Dane z GUS REGON' : 'Dane z białej listy VAT'}
              </Text>
            )}
          </Group>
          {c && onApply && (
            <Button size="compact-xs" variant="light" leftSection={<IconDownload size={12} />} onClick={() => onApply(d)}>
              Wstaw dane z rejestru
            </Button>
          )}
        </Group>
        {c ? (
          <Text size="sm">
            {c.name}
            {address(c) && <Text span c="dimmed" size="xs"> · {address(c)}</Text>}
            {(c.regon || c.krs) && <Text span c="dimmed" size="xs"> · {[c.regon && `REGON ${c.regon}`, c.krs && `KRS ${c.krs}`].filter(Boolean).join(' · ')}</Text>}
          </Text>
        ) : (
          <Text size="xs" c="dimmed">Nie znaleziono firmy w rejestrach — wpisz dane ręcznie.</Text>
        )}
        {warning && (
          <Group gap={4} wrap="nowrap">
            <IconAlertTriangle size={14} color="var(--mantine-color-orange-6)" />
            <Text size="xs" c="orange.7">
              {warning} — sprawdź klienta przed podpisaniem umowy.
            </Text>
          </Group>
        )}
        {[d.vatError, d.regonError].filter(Boolean).map((e) => (
          <Text key={e} size="xs" c="dimmed">
            {e}
          </Text>
        ))}
      </Stack>
    </Paper>
  );
}

/** Uzupełnia puste pola danymi z rejestru (wypełnionych przez użytkownika nie nadpisuje; `force` — nadpisuje). */
export function fillFromRegistry<T extends { name: string; street: string; postalCode: string; city: string }>(cur: T, d: NipLookupDto, force = false): T {
  const c = d.company;
  if (!c) return cur;
  const addressEmpty = !cur.street.trim() && !cur.postalCode.trim() && !cur.city.trim();
  return {
    ...cur,
    name: force || !cur.name.trim() ? c.name : cur.name,
    ...(force || addressEmpty ? { street: c.street, postalCode: c.postalCode, city: c.city } : {}),
  };
}
